import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { api } from "../../lib/api";
import { useSEO } from "../../lib/seo";
import { MapPin, Star, CurrencyDollar, User } from "@phosphor-icons/react";

export default function VirtualFreelancersList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cats, setCats] = useState({ service_types: [] });
  const [filters, setFilters] = useState({
    service: searchParams.get("service") || "",
    country: searchParams.get("country") || "",
    language: searchParams.get("language") || "",
    rate_max: searchParams.get("rate_max") || "",
    q: searchParams.get("q") || "",
  });

  useSEO({
    title: "Browse Virtual Freelancers",
    description: "Browse pre-vetted virtual freelancers: VAs, web developers, designers, marketers and more. Hire remotely.",
    path: "/virtual/freelancers",
  });

  useEffect(() => {
    api.get("/virtual/categories").then((r) => setCats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    api.get("/virtual/profiles", { params })
      .then((r) => setProfiles(r.data))
      .catch(() => setProfiles([]))
      .finally(() => setLoading(false));
    setSearchParams(params);
  }, [filters, setSearchParams]);

  const upd = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <section className="pt-24 pb-12 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/virtual" className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 hover:text-slate-900">← Hire Virtual Staff</Link>
        <h1 className="font-display text-4xl mt-2 mb-8">Browse freelancers</h1>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-8 p-4 bg-slate-50 border border-slate-200">
          <input value={filters.q} onChange={(e) => upd("q", e.target.value)} placeholder="Search…" className="col-span-2 md:col-span-1 px-3 py-2 border border-slate-300 text-sm" data-testid="vff-q" />
          <select value={filters.service} onChange={(e) => upd("service", e.target.value)} className="px-3 py-2 border border-slate-300 text-sm" data-testid="vff-service">
            <option value="">All services</option>
            {cats.service_types.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input value={filters.country} onChange={(e) => upd("country", e.target.value)} placeholder="Country" className="px-3 py-2 border border-slate-300 text-sm" data-testid="vff-country" />
          <input value={filters.language} onChange={(e) => upd("language", e.target.value)} placeholder="Language" className="px-3 py-2 border border-slate-300 text-sm" data-testid="vff-language" />
          <input type="number" value={filters.rate_max} onChange={(e) => upd("rate_max", e.target.value)} placeholder="Max rate CAD" className="px-3 py-2 border border-slate-300 text-sm" data-testid="vff-rate" />
        </div>

        {loading ? (
          <div className="text-center py-16 text-slate-500">Loading…</div>
        ) : profiles.length === 0 ? (
          <div className="border border-dashed border-slate-300 p-12 text-center">
            <User size={32} className="mx-auto text-slate-400 mb-2" />
            <p className="text-sm text-slate-600">No freelancers match your filters yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {profiles.map((p) => (
              <Link key={p.user_id} to={`/virtual/freelancers/${p.user_id}`} className="border border-slate-200 p-5 hover:border-brand hover:-translate-y-1 transition-all" data-testid={`vff-${p.user_id}`}>
                <div className="flex items-center gap-3 mb-3">
                  {p.picture ? <img src={p.picture} alt={p.name} className="w-12 h-12 rounded-full" /> : <div className="w-12 h-12 bg-slate-200 rounded-full" />}
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate">{p.name}</div>
                    <div className="text-xs text-slate-500 truncate">{p.headline}</div>
                  </div>
                </div>
                <p className="text-sm text-slate-600 line-clamp-2 mb-3">{p.bio}</p>
                <div className="flex flex-wrap gap-1 mb-3">
                  {(p.services || []).slice(0, 3).map((s) => (
                    <span key={s} className="text-[10px] uppercase tracking-wider font-semibold text-accent-red bg-orange-50 px-2 py-0.5">{s}</span>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                  <span className="flex items-center gap-1"><MapPin size={12} weight="bold" /> {p.country}</span>
                  <span className="flex items-center gap-1"><CurrencyDollar size={12} weight="bold" /> ${p.hourly_rate}/hr</span>
                  {p.rating > 0 && <span className="flex items-center gap-1"><Star size={12} weight="fill" className="text-accent-red" /> {p.rating.toFixed(1)}</span>}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
